// src/services/api.js
import axios from 'axios';
import API from '../components/config';
import { getCurrentUserToken } from './firebaseAuth';
import { auth } from '../config/firebase';

// Create axios instance
const api = axios.create({
  baseURL: API,
  headers: {
    'Content-Type': 'application/json',
    'X-Requested-With': 'XMLHttpRequest',
  },
  // Enable credentials to include cookies in cross-origin requests
  withCredentials: true,
});

// Add request interceptor to include Firebase auth token
api.interceptors.request.use(
  async (config) => {
    // Skip auth for health check and public endpoints
    if (config.url?.includes('/health') || config.url?.includes('/public')) {
      return config;
    }
    
    // Get fresh Firebase token
    try {
      let token = await getCurrentUserToken();
      
      // If getCurrentUserToken returns null, try direct access to auth.currentUser
      if (!token && auth.currentUser) {
        console.log('[API] getCurrentUserToken returned null, trying auth.currentUser directly...');
        token = await auth.currentUser.getIdToken(true); // Force refresh
      }
      
      // If still no token, wait a bit for Firebase to initialize (sometimes there's a race condition)
      if (!token && !auth.currentUser) {
        console.warn('[API] No Firebase user found, waiting 100ms for auth to initialize...');
        await new Promise(resolve => setTimeout(resolve, 100));
        if (auth.currentUser) {
          token = await auth.currentUser.getIdToken();
        }
      }
      
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
      } else {
        console.error('[API] No Firebase token available for:', config.url);
        console.error('[API] auth.currentUser:', auth.currentUser ? `exists (${auth.currentUser.uid})` : 'null');
        // Don't fail the request, but log the issue
      }
    } catch (error) {
      console.error('[API] Error getting Firebase token:', error);
      // Try one more time with force refresh
      try {
        if (auth.currentUser) {
          const token = await auth.currentUser.getIdToken(true);
          config.headers['Authorization'] = `Bearer ${token}`;
          console.log('[API] Got token after error recovery');
        }
      } catch (retryError) {
        console.error('[API] Retry also failed:', retryError);
      }
    }
    
    // For preflight requests
    if (config.method === 'options') {
      config.headers['Access-Control-Request-Method'] = config.method;
      config.headers['Access-Control-Request-Headers'] = 'Content-Type, Authorization';
    }
    return config;
  },
  error => Promise.reject(error)
);

// Add response interceptor to handle CORS errors
api.interceptors.response.use(
  response => response,
  error => {
    // If the error is due to CORS
    if (error.message && error.message.includes('Network Error')) {
      console.error('CORS Error:', error);
      // You could implement a fallback strategy here
      // For example, retry with a different approach or notify the user
    }
    return Promise.reject(error);
  }
);

export default api;
